/*
 * ZusatzLeistungen.tsx
 *
 * Created by Dr. Maximillian Dornseif 2023-11-11 in huWaWi3 33.8.0
 * Copyright (c) 2023 HUDORA GmbH
 */

import { LadeChecker } from '@hudora/hd-react-components'
import { ValueRender } from '@hudora/react-jsonschema-valuerender'
import { Typography } from '@mui/material'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { DocumentNode } from 'graphql'
import React from 'react'
import { gql, useQuery } from 'urql'

import { ZUSATZLEISTUNG_FRAGMENT } from '../../fragments'
import { useAuditPanel3 } from '../../lib/hooks/useAuditPanel3'
import { useEditPanel3 } from '../../lib/hooks/useEditPanel3'
import { useRohdatenPanel3 } from '../../lib/hooks/useRohdatenPanel3'
import { LinkRender } from '../../lib/hd-link'

// Declaring type of props - see "Typing Component Props" for more examples
type DisployZusatzLeistungenProps = {
  auftrag: Record<string, any> // Auftrag oder Reservierung
  nr: string
  gqlQuery: DocumentNode
} /* use `interface` if exporting so that consumers can extend */

export function ZusatzLeistungen({ auftrag, nr, gqlQuery }: DisployZusatzLeistungenProps) {
  const [{ data, fetching, error }] = useQuery({
    query: gqlQuery,
    variables: { nr },
  })
  const [, editPanel, , renderEditPanelButton] = useEditPanel3({
    schemaName: 'HwAuftragsZusatzleistung',
    mutation: ZUSATZLEISRTUNG_PATCH_MUTATION,
    entity: undefined,
  })
  const [, auditPanel, , renderAuditPanelButton] = useAuditPanel3({
    defaultName: 'Zusatzleistung',
    designator: undefined,
    entity: undefined,
  })
  const [, rohdatenPanel, , renderRohdatenPanelButton] = useRohdatenPanel3({
    schemaName: 'HwAuftragsZusatzleistung',
    entity: undefined,
  })

  return (
    <LadeChecker loading={fetching} error={error} label={`Lade Zusatzleistungen`}>
      {editPanel}
      {auditPanel}
      {rohdatenPanel}
      <section>
        <Typography color="text.secondary" gutterBottom variant="h5" component="h3">
          Zusatzleistungen
        </Typography>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell>№</TableCell>
                <TableCell>Name</TableCell>
                <TableCell align="right">Wert €</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.entity?.zusatzleistungen?.map((pos) => (
                <TableRow key={pos.posnr}>
                  <TableCell>
                    {pos.erledigt ? (
                      <Chip label="erledigt" size="small" color="success" variant="outlined" />
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <LinkRender value={pos.artnr} />
                  </TableCell>
                  <TableCell>{pos?.name}</TableCell>
                  <TableCell title={pos.preisherkunft} style={{ whiteSpace: 'nowrap' }} align="right">
                    <ValueRender value={pos.preis} format="eurocent" />
                  </TableCell>
                  <TableCell>
                    {pos.erledigt ? null : renderEditPanelButton(pos)}
                    {renderAuditPanelButton(pos)}
                    {renderRohdatenPanelButton(pos)}
                  </TableCell>
                </TableRow>
              ))}
              {/* {% if pos.infotext_kunde %}
             <tr><th>Kundenreferenz:</th><td colspan="4">{{ pos.infotext_kunde|e }}</td></tr>
            {% endif %} */}
              {/* {% else %}
             <tr><td><strong>keine Positionen vorhanden</strong></td></tr> */}
            </TableBody>
          </Table>
        </TableContainer>
      </section>
    </LadeChecker>
  )
}

const ZUSATZLEISRTUNG_PATCH_MUTATION = gql`
  mutation ZusatzleistungPositionPatch(
    $designator: ID!
    $input: HwAuftragsZusatzleistungPatchInput!
    $oldRev: ID!
  ) {
    patchInfo: hwAuftragsZusatzleistungPatch(designator: $designator, input: $input, oldRev: $oldRev) {
      old {
        ...zusatzleistungFragment
      }
      new {
        ...zusatzleistungFragment
      }
      oldRev
      newRev
      duration
      success
      errors
      audit {
        created_at
        typ
        uid
        user
        text
        new
        old
      }
    }
  }
  ${ZUSATZLEISTUNG_FRAGMENT}
`
